/**
 * This composable returns an author page object constructed from outer blocks.
 * It fetches author data from Sanity and programmatically builds the page layout
 * using the same pattern as useSurpriseBagData and useJobPostData.
 */
import { OBJECT, DOCUMENT } from '@pkg/config/sanity/schemas';
import { THEME } from '@pkg/config/misc/theme';
import { authorBioForLocale, authorHasBioForLocale } from '@pkg/sanity-utils/authorBioQueries';
import { translatedFieldQuery } from '@pkg/sanity-utils/translatedFieldQuery';
import { portableTextWithReferences } from '@pkg/sanity-utils/portableTextWithReferences';
import { documentToFieldName } from '@pkg/sanity-utils/documentToFieldName';
import type { PageSeo } from '@pkg/sanity-utils/types/seo';
import type { AuthorLocalised } from '@pkg/sanity-utils/types/author';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import { LISTING_TYPES } from '~/app/types/listingTypes';
import { useSanityPageSeoMeta } from '~/app/composables/useSanityPageSeoMeta';
import { useAuthorJsonLd } from '~/app/composables/useAuthorJsonLd';

interface UseAuthorDataParams {
  slug: string;
  locale: string;
}

const authorQuery = groq`{
    "page": *[_type == "${DOCUMENT.AUTHORS}" && slug.current == $slug && ${authorHasBioForLocale}][0] {
    _type,
    _id,
    name,
    slug,
    authorType,
    foundingDate,
    expertise,
    areaServed,
    image,
    ${translatedFieldQuery('jobTitle')},
    "bio": ${authorBioForLocale}.value[] {
      ${portableTextWithReferences}
    },
    seoSocial,
  },
}`;

export async function useAuthorData({ slug, locale }: UseAuthorDataParams) {
  // Must run before await — useI18n/useHead need an active setup context
  const authorRef = shallowRef<AuthorLocalised | null>(null);
  useAuthorJsonLd(authorRef);

  const { data: response } = await useSanityData(authorQuery, {
    slug,
    locale,
  });

  const author = ((toRaw(response.value) as any)?.page ?? null) as AuthorLocalised | null;
  authorRef.value = author;

  if (!author) {
    return {
      data: ref({ page: undefined }),
    };
  }

  // Construct sub-hero block
  const subHeroBlock = {
    _type: OBJECT.MODULAR_OUTER_BLOCK_SUB_HERO,
    _key: 'author-sub-hero',
    theme: THEME.DARK,
    variant: 'mediaNone',
    content: [
      {
        _type: OBJECT.MODULAR_INNER_BLOCK_TITLE,
        _key: 'author-title',
        title: [
          {
            _type: 'block',
            children: [
              {
                _type: 'span',
                marks: [],
                text: author.name,
              },
            ],
            markDefs: [],
            style: 'normal',
          },
        ],
        size: 'large',
        variant: 'center',
        preTitle: author.jobTitle,
      },
      {
        _type: OBJECT.MODULAR_INNER_BLOCK_PROFILE_PICTURE,
        _key: 'author-profile-picture',
        size: 5,
        class: 'text-center',
        person: {
          _key: author.name,
          name: author.name,
          jobTitle: author.jobTitle,
          image: author.image,
        },
      },
    ],
  };

  // Construct bio section block
  const bioSectionBlock = {
    _type: OBJECT.MODULAR_OUTER_BLOCK_SECTION,
    _key: 'author-bio-section',
    theme: THEME.LIGHT,
    content: [
      {
        _type: OBJECT.MODULAR_INNER_BLOCK_BASE_TEXT,
        _key: 'author-bio-text',
        text: author.bio,
      },
    ],
  };

  // Construct articles listing block
  const articlesListingBlock = {
    _type: OBJECT.MODULAR_OUTER_BLOCK_SECTION,
    _key: 'author-articles-section',
    theme: THEME.LIGHT,
    content: [
      {
        _type: OBJECT.MODULAR_INNER_BLOCK_LISTING,
        _key: 'author-articles-listing',
        listingDocument: DOCUMENT.ABOUT_FOOD_WASTE_BLOG,
        listingType: LISTING_TYPES.CATEGORY,
        [`listingFilter_${documentToFieldName(DOCUMENT.ABOUT_FOOD_WASTE_BLOG)}`]: DOCUMENT.AUTHORS,
        categorySelection: [author.slug.current],
      },
    ],
  };

  // Build seoSocial - use custom if available, otherwise fallback
  const seoSocial: PageSeo = author.seoSocial ?? {
    metaTitle: author.name,
    ogTitle: author.name,
    metaDescription: author.jobTitle || '',
    ogDescription: author.jobTitle || '',
  };

  // Apply SEO meta
  if (author.seoSocial?.metaTitle || author.seoSocial?.metaDescription) {
    useSanityPageSeoMeta(author.seoSocial);
  } else if (author.name) {
    useSeoMeta({
      title: author.name,
      ogTitle: author.name,
      description: author.jobTitle || '',
      ogDescription: author.jobTitle || '',
    });
  }

  // Set body class for dark first block theme
  useHead({ bodyAttrs: { class: `first-block-theme--${THEME.DARK}` } });

  return {
    data: ref({
      page: {
        _type: DOCUMENT.PAGES,
        content: [subHeroBlock, bioSectionBlock, articlesListingBlock],
        seoSocial,
      },
    }),
  };
}
